import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {useGetCategoriesQuery, useGetUserQuery, useUpdateUserMutation, useUploadAvatarMutation} from "../../../store/reducers/adminApi";
import Image from "react-bootstrap/Image";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table";
import moment from "moment";
import {Link} from "react-router-dom";
import Button from "react-bootstrap/Button";
import ToggleButton from "react-bootstrap/ToggleButton";
import Form from "react-bootstrap/Form";
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Select, {MultiValue} from 'react-select'
import makeAnimated from 'react-select/animated';
import {constants} from "../../../config/constants";
import ToastBar, {ToastData} from "../../../components/ToastBar/toastbar";
import chatSession from "../../../../../src/routes/chatSession";

export function Psychic() {

    const tags = useMemo(() => {
        return Object.keys(constants.UserTags).map((key, id) => ({
            id,
            label: key,
            value: constants.UserTags[key]
        }))
    }, []);

    let {userID} = useParams();
    const [currentTag, setCurrentTag] = useState("standard")
    const [allChatSessions, setAllChatSessions] = useState({});
    const [show, setShow] = useState(false);
    const [subscriptionOnly, setSubscriptionOnly] = useState(false);
    const [approved, setApproved] = useState(false);
    const [stripeActive, setStripeActive] = useState(false);
    const [available, setAvailable] = useState(false);
    const [ToastMessage, setToastMessage] = useState<ToastData>({
        title: null,
        body: null,
        type: null
    })
    const [selectedCategories, setSelectedCategories] = React.useState<MultiValue<{
        value: string,
        label: string
    }>>([]);
    const animatedComponents = makeAnimated();

    const {data: user, refetch} = useGetUserQuery(userID, {
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
    });

    const {data: categories} = useGetCategoriesQuery(null)
    const [updateUser, updateUserResponse] = useUpdateUserMutation();
    const [uploadAvatar, uploadAvatarResponse] = useUploadAvatarMutation();
    const [payoutRate, setPayoutRate] = useState<any>(0);
    const [usernameDraft, setUsernameDraft] = useState<string>("");
    const [bioDraft, setBioDraft] = useState<string>("");
    const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);

    const setEnabled = () => {
        updateUser({userID: user.userID, enabled: !user.enabled}).unwrap().then(() => {
            refetch()
            showToast('Action', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e.message, 'danger')
        });
    };

    const toggleSubscriptionOnly = () => {
        updateUser({userID: user.userID, subscription_only: !subscriptionOnly}).unwrap().then(() => {
            refetch()
            showToast('Subscription Access', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e.message, 'danger')
        });
    };

    const toggleApproved = () => {
        updateUser({userID: user.userID, approved: !approved}).unwrap().then(() => {
            refetch()
            showToast('Approved Status', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e?.message || 'Something went wrong!', 'danger')
        });
    };

    const toggleStripeActive = () => {
        updateUser({userID: user.userID, stripe_active: !stripeActive}).unwrap().then(() => {
            refetch()
            showToast('Stripe Active', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e?.message || 'Something went wrong!', 'danger')
        });
    };

    const toggleAvailable = () => {
        updateUser({userID: user.userID, available: !available}).unwrap().then(() => {
            refetch()
            showToast('Availability', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e?.message || 'Something went wrong!', 'danger')
        });
    };

    const savePayoutRate = () => {
        updateUser({userID: user.userID, payout_rate: payoutRate}).unwrap().then(() => {
            refetch()
            showToast('Payout Rate', 'Updated Successfully', 'success')
        }).catch((e) => [
            showToast('Error', e.message, 'danger')
        ])
    };
    const saveUsername = () => {
        updateUser({userID: user.userID, username: usernameDraft}).unwrap().then(() => {
            refetch()
            showToast('Username', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e?.data?.error || e?.message || 'Something went wrong!', 'danger')
        });
    };

    const saveBio = () => {
        updateUser({userID: user.userID, bio: bioDraft}).unwrap().then(() => {
            refetch()
            showToast('Bio', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e?.data?.error || e?.message || 'Something went wrong!', 'danger')
        });
    };

    const onAvatarFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setPendingAvatar(reader.result as string);
        reader.onerror = () => showToast('Error', 'Failed to read image file', 'danger');
        reader.readAsDataURL(file);
    };

    const saveAvatar = () => {
        if (!pendingAvatar) return;
        uploadAvatar({userID: user.userID, image: pendingAvatar}).unwrap().then(() => {
            setPendingAvatar(null);
            refetch();
            showToast('Profile Picture', 'Updated Successfully', 'success');
        }).catch((e) => {
            showToast('Error', e?.data?.error || e?.message || 'Upload failed', 'danger');
        });
    };

    const cancelAvatar = () => setPendingAvatar(null);

    const updateCategories = () => {
        updateUser({userID: user.userID, userCategories: selectedCategories.map(c => c.value)}).unwrap().then(() => {
            refetch()
            showToast('Category', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e.message, 'danger')
        });
    };
    useEffect(() => {
        setPayoutRate(prevValue => user ? user.payout_rate : prevValue)
        setCurrentTag(prevValue => user ? user.tag : prevValue)
        setSubscriptionOnly(prevValue => user ? user.subscription_only : prevValue)
        setApproved(prevValue => user ? user.approved : prevValue)
        setStripeActive(prevValue => user ? user.stripe_active : prevValue)
        setAvailable(prevValue => user ? user.available : prevValue)
        setUsernameDraft(prevValue => user ? (user.username || "") : prevValue)
        setBioDraft(prevValue => user ? (user.bio || "") : prevValue)
        setSelectedCategories(prevValue => user ? user.userCategories.map(c => ({
            value: c.category.id,
            label: c.category.name
        })) : prevValue)
    }, [user]);

    const setUserTag = (e) => {
        updateUser({userID: user.userID, tag: e.target.value}).unwrap().then(() => {
            refetch()
            showToast('Psychic Account', 'Updated Successfully', 'success')
        }).catch((e) => {
            showToast('Error', e.message, 'danger')
        });
    }
    const options = useMemo(() => {
        return categories?.map(category => ({value: category.id, label: category.name})) || []
    }, [categories])

    const showToast = (title: string, body: string, type: 'success' | 'danger' | null) => {
        setToastMessage({
            title,
            body,
            type
        })
        setShow(true)
    }

    // let chatSessions = {};
    //
    // user?.transfers?.forEach(transfer => {
    //     if (transfer.chatSession) {
    //         chatSessions[transfer.chatSession.chatSessionID] = transfer.chatSession;
    //     }
    // });
    useEffect(() => {
        // First handle existing ones from transfers (current implementation)
        let transferChatSessions = {};
        if (user?.transfers) {
            user.transfers.forEach(transfer => {
                if (transfer.chatSession) {
                    transferChatSessions[transfer.chatSession.chatSessionID] = transfer.chatSession;
                }
            });
        }

        // Now handle chat sessions from the user's chats
        let chatSessionsFromChats = {...transferChatSessions};
        if (user?.chats) {
            user.chats.forEach(chat => {
                if (chat.chatSessions) {
                    chat.chatSessions.forEach(chatSession => {
                        chatSessionsFromChats[chatSession.chatSessionID] = chatSession;
                    });
                }
            });
        }

        setAllChatSessions(chatSessionsFromChats);
    }, [user]);

    return (
        <div>
            {user && (
                <React.Fragment>
                    <div className="d-flex flex-row align-items-center w-75 justify-content-between">
                        <div className="d-flex flex-column align-items-start">
                            <Image
                                roundedCircle
                                src={pendingAvatar || user.avatar}
                                width={150}
                                height={150}
                            />
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={onAvatarFileSelected}
                                className="mt-2"
                                style={{maxWidth: 250}}
                            />
                            {pendingAvatar && (
                                <div className="mt-2">
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={saveAvatar}
                                        disabled={uploadAvatarResponse.isLoading}
                                        className="me-2"
                                    >
                                        {uploadAvatarResponse.isLoading ? "Uploading…" : "Save Photo"}
                                    </Button>
                                    <Button variant="secondary" size="sm" onClick={cancelAvatar}>
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </div>
                        <ButtonGroup onChange={setUserTag}>
                            {tags.map((tag) => (
                                <ToggleButton
                                    className="btn-sm"
                                    id={`radio-${tag.id}`}
                                    type="radio"
                                    variant="outline-dark"
                                    checked={currentTag == tag.value}
                                    value={tag.value}
                                    onChange={(e) => setCurrentTag(e.currentTarget.value)}
                                >{tag.label}</ToggleButton>
                            ))}

                        </ButtonGroup>
                    </div>

                    <ToastBar tostData={ToastMessage} setToastData={setToastMessage}/>

                    <Table>
                        <tbody>
                        <tr>
                            <td>Status</td>
                            <td>
                                <Badge bg={user.enabled ? "success" : "danger"}>
                                    {user.enabled ? "Enabled" : "Disabled"}
                                </Badge>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={3}>
                                <h6 className="mb-0 mt-2">Advisor Settings</h6>
                            </td>
                        </tr>
                        <tr>
                            <td>Approved</td>
                            <td>
                                <Form.Check
                                    id="approved-switch"
                                    type="switch"
                                    checked={approved}
                                    onChange={() => toggleApproved()}
                                    label={approved ? "Approved to take sessions" : "Not approved"}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Stripe Active</td>
                            <td>
                                <Form.Check
                                    id="stripe-active-switch"
                                    type="switch"
                                    checked={stripeActive}
                                    onChange={() => toggleStripeActive()}
                                    label={stripeActive ? "Payment processing enabled" : "Payment processing disabled"}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Subscription Only</td>
                            <td>
                                <Form.Check
                                    id="subscription-only-switch"
                                    type="switch"
                                    checked={subscriptionOnly}
                                    onChange={() => toggleSubscriptionOnly()}
                                    label={subscriptionOnly ? "Only accepts subscription users" : "Accepts all users"}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Available</td>
                            <td>
                                <Form.Check
                                    id="available-switch"
                                    type="switch"
                                    checked={available}
                                    onChange={() => toggleAvailable()}
                                    label={available ? "Currently accepting sessions" : "Not accepting sessions"}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={3}>
                                <hr className="my-2" />
                            </td>
                        </tr>
                        <tr>
                            <td>Email</td>
                            <td>{user.email}</td>
                        </tr>
                        <tr>
                            <td>Username</td>
                            <td>
                                <Form.Control
                                    value={usernameDraft}
                                    onChange={(e) => setUsernameDraft(e.target.value)}
                                />
                            </td>
                            <td>
                                <Button
                                    variant="success"
                                    onClick={saveUsername}
                                    disabled={updateUserResponse.isLoading || usernameDraft === (user.username || "")}
                                >
                                    Save
                                </Button>
                            </td>
                        </tr>
                        <tr>
                            <td>Created On</td>
                            <td>{moment(user.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>
                        </tr>
                        <tr>
                            <td>Bio</td>
                            <td>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={bioDraft}
                                    onChange={(e) => setBioDraft(e.target.value)}
                                />
                            </td>
                            <td>
                                <Button
                                    variant="success"
                                    onClick={saveBio}
                                    disabled={updateUserResponse.isLoading || bioDraft === (user.bio || "")}
                                >
                                    Save
                                </Button>
                            </td>
                        </tr>
                        <tr>
                            <td>Categories</td>
                            <td className="col-md-6">
                                <Select
                                    onChange={setSelectedCategories}
                                    closeMenuOnSelect={false}
                                    components={animatedComponents}
                                    value={selectedCategories}
                                    defaultValue={selectedCategories}
                                    isMulti
                                    options={options}
                                />
                            </td>
                            <td>
                                <Button variant={"success"} onClick={updateCategories}>
                                    Save
                                </Button>
                            </td>
                        </tr>

                        <tr>
                            <td>Payout Rate</td>
                            <td>
                                <Form.Control
                                    value={payoutRate}
                                    onChange={(e) => setPayoutRate(e.target.value)}
                                    type="number"
                                />
                            </td>
                            <td>
                                <Button variant={"success"} onClick={savePayoutRate}>
                                    Save
                                </Button>
                            </td>
                        </tr>
                        <tr>
                            <td>Chat Requests</td>
                            <td>{user?.chatRequestsReceived?.length}</td>
                        </tr>
                        <tr>
                            <td>Actions</td>
                            <td>
                                <Button variant={user.enabled ? "danger" : "success"} onClick={setEnabled}>
                                    {user.enabled ? "Disable" : "Enable"} User
                                </Button>
                            </td>
                        </tr>
                        </tbody>
                    </Table>

                    <h5>Chat sessions</h5>
                    <Table>
                        <thead>
                        <tr>
                            <th>Date</th>
                            <th>Session ID</th>
                            <th>Chat Duration</th>
                            <th>Chat Rate</th>
                            <th>Revenue</th>
                            <th>Psychic Share</th>
                            <th>Chat</th>
                        </tr>
                        </thead>
                        <tbody>
                        {/*Iterate over chatSessions object: */}
                        {Object.keys(allChatSessions).map((chatID) => {
                            const chatSession = allChatSessions[chatID];
                            return (
                                <tr key={chatSession.id}>
                                    <td>{moment(chatSession.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>

                                    <td>
                                        {chatSession.chatSessionID}
                                    </td>
                                    <td>
                                        {chatSession.active_time} seconds
                                    </td>
                                    <td>
                                        ${(chatSession.chat_rate * 0.01).toFixed(2)}
                                    </td>
                                    <td>
                                        ${((chatSession.chat_rate * 0.01) * (chatSession.active_time / 60)).toFixed(2)}
                                    </td>
                                    <td>
                                        ${(chatSession.chat_rate * (chatSession.active_time / 60) * user.payout_rate * 0.01).toFixed(2)}
                                    </td>
                                    <td>
                                        <Link to={`/chat/${chatSession.chatID}`}>
                                            {chatSession.chatID}
                                        </Link>
                                    </td>
                                </tr>
                            )
                        })}
                        </tbody>
                    </Table>

                    <h5>Transfers</h5>
                    <Table>
                        <thead>
                        <tr>
                            <th>Date</th>
                            <th>Transfer Amount</th>
                            <th>Status</th>
                            <th>Customer Charge</th>
                            <th>Stripe balance on payout</th>
                            <th>Session ID</th>
                            <th>Chat</th>
                        </tr>
                        </thead>
                        <tbody>
                        {user?.transfers?.map((transfer) => {
                            return (
                                <tr key={transfer.transferID}>
                                    <td>{moment(transfer.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>
                                    <td>${(transfer.amount / 100).toFixed(2)}</td>
                                    <td>{transfer.status}</td>
                                    <td>${transfer.source_charge_amount == -1 ? "CREDIT" : transfer.source_charge_amount * 0.01}</td>
                                    <td>${transfer.stripe_balance}</td>
                                    <td>
                                        {transfer && transfer.chatSession && (
                                            <div>
                                                {transfer.chatSession.chatSessionID}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {transfer && transfer.chatSession && (
                                            <Link to={`/chat/${transfer.chatSession.chatID}`}>
                                                {transfer.chatSession.chatID}
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </React.Fragment>
            )}
        </div>
    );
}